import React, { useState } from 'react';
import { X, MinusCircle, PlusCircle } from 'lucide-react';
import { StockLogo } from '../../utils/stockLogo';
import { useCurrency } from '../../contexts/CurrencyContext';

const AssetQuantityModal = ({ asset, onClose, onUpdate, allCategories, onMove }) => {
  const { formatAmount } = useCurrency();
  const [quantity, setQuantity] = useState(asset.quantity || 0);
  const [avgCost, setAvgCost] = useState(asset.avgCost ? parseFloat(asset.avgCost.toFixed(4)) : 0);
  const [isLoading, setIsLoading] = useState(false);
  
  // Category Move States
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');

  const isCurrency = asset.symbol?.endsWith('CURRENCY');
  const assetCurrency = isCurrency ? asset.symbol.split('.')[0] : (asset.currency || 'USD');
  const step = "0.0001";

  const activeCategories = allCategories?.filter(c => c.name !== 'Diğer') || [];
  const activeSubCategories = selectedCategory 
    ? allCategories?.find(c => c.name === selectedCategory)?.subCategories || []
    : [];

  const handleQuantityChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setQuantity(value);
    }
  };

  const handleAvgCostChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setAvgCost(value);
    }
  };

  const handleIncrement = () => {
    setQuantity(prev => parseFloat((prev + 1).toFixed(4)));
  };

  const handleDecrement = () => {
    setQuantity(prev => Math.max(0, parseFloat((prev - 1).toFixed(4))));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // 1. Önce taşıma işlemi varsa yap
      if (selectedCategory && onMove) {
          await onMove(asset.symbol, selectedCategory, selectedSubCategory || null);
      }

      // 2. Güncelleme işlemini yap
      const updateData = {
        quantity,
        currency: assetCurrency,
        exchange: asset.exchange || (isCurrency ? 'IDEALPRO' : 'ARCA'),
        secType: isCurrency ? 'CASH' : 'STK',
        symbol: asset.symbol
      };

      // Currency için özel değerler
      if (isCurrency) {
        updateData.avgCost = 1;
        updateData.value = quantity;
      } else {
        updateData.avgCost = avgCost;
        updateData.value = quantity * avgCost;
      }

      await onUpdate(asset.symbol, updateData);
      onClose();
    } catch (error) {
      console.error('Update error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDisplayValue = () => {
    if (isCurrency) {
      return formatAmount(quantity, assetCurrency);
    }
    return `${quantity} adet`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            {isCurrency ? (
              <img 
                src={`/images/currencys/${assetCurrency.toLowerCase()}.png`}
                alt={assetCurrency}
                className="h-8 w-8"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<span className='text-lg font-medium'>${assetCurrency}</span>`;
                }}
              />
            ) : (
              <StockLogo symbol={asset.symbol} symbolInfo={asset} size="lg" />
            )}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {asset.description || asset.symbol}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isCurrency ? assetCurrency : asset.symbol}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {isCurrency ? 'Para Miktarı' : 'Varlık Miktarı'}
              </label>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={handleDecrement}
                  className="text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <MinusCircle className="w-6 h-6" />
                </button>
                
                <div className="flex-1">
                  <input
                    type="number"
                    value={quantity}
                    onChange={handleQuantityChange}
                    min="0"
                    step={step}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {getDisplayValue()}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleIncrement}
                  className="text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <PlusCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            {!isCurrency && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ortalama Maliyet
                </label>
                <input
                  type="number"
                  value={avgCost}
                  onChange={handleAvgCostChange}
                  min="0"
                  step={step}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            )}

            {/* Kategori Taşıma Bölümü */}
            {onMove && activeCategories.length > 0 && !isCurrency && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Kategori Değiştir</h4>
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Yeni Kategori</label>
                            <select
                                value={selectedCategory}
                                onChange={(e) => {
                                    setSelectedCategory(e.target.value);
                                    setSelectedSubCategory('');
                                }}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            >
                                <option value="">Seçiniz...</option>
                                {activeCategories.map(cat => (
                                    <option key={cat.name} value={cat.name}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                        
                        {activeSubCategories.length > 0 && (
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Alt Kategori (Opsiyonel)</label>
                                <select
                                    value={selectedSubCategory}
                                    onChange={(e) => setSelectedSubCategory(e.target.value)}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                                >
                                    <option value="">Seçiniz...</option>
                                    {activeSubCategories.map(sub => (
                                        <option key={sub.name} value={sub.name}>{sub.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <button
                            type="button"
                            disabled={true} // Buton devre dışı bırakıldı, işlem "Güncelle" ile yapılacak
                            className="hidden w-full px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-transparent rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30"
                        >
                            {isLoading ? 'Taşınıyor...' : 'Bu Kategoriye Taşı'}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        * Kategoriyi seçtikten sonra işlemi tamamlamak için aşağıdaki "Güncelle" butonuna basınız.
                    </p>
                </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Güncelleniyor...' : 'Güncelle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssetQuantityModal;